// libs
import { useApolloClient } from 'hooks/useApolloClient'
// types
import {
  IDashboard,
  IDashboardItem,
  IMutationResponse,
  IDashboardCreateInput,
  IDashboardUpdateInput,
  IDashboardDeleteInput,
  IGetDashboardsResponse,
  IDashboardItemCreateInput,
  IDashboardItemUpdateInput,
  IGetDashboardItemsResponse,
  IDashboardUpdateMutationArgs,
  IDashboardCreateMutationArgs,
  IDashboardDeleteMutationArgs,
  IDashboardItemUpdateMutationArgs,
  IDashboardItemCreateMutationArgs,
  IDashboardItemDeleteMutationArgs
} from 'types/graphql'
// graphql
import {
  DASHBOARD_CREATE_MUTATION,
  DASHBOARD_DELETE_MUTATION,
  DASHBOARD_ITEM_CREATE_MUTATION,
  DASHBOARD_ITEM_DELETE_MUTATION,
  DASHBOARD_ITEM_UPDATE_MUTATION,
  DASHBOARD_UPDATE_MUTATION
} from 'graphql/dashboards/mutations'
import { DASHBOARDS_ITEMS_QUERY, DASHBOARDS_QUERY } from 'graphql/dashboards/queries'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

export const useDashboardsAPI = () => {
  const { client } = useApolloClient()

  const getDashboards = async () => {
    try {
      const response = await client.query<{ dashboards: IGetDashboardsResponse }>({
        query: DASHBOARDS_QUERY
      })

      return response.data.dashboards.data
    } catch (e) {
      console.log('[useDashboardsAPI.getDashboards]: ', parseGraphQLErrorMessages(e))
    }
  }

  const getDashboardItems = async (id: string) => {
    try {
      const response = await client.query<{ dashboardItems: IGetDashboardItemsResponse }>({
        query: DASHBOARDS_ITEMS_QUERY,
        variables: {
          input: {
            dashboardId: id
          }
        }
      })

      return response.data.dashboardItems.data
    } catch (e) {
      console.log('[useDashboardsAPI.getDashboardItems]: ', parseGraphQLErrorMessages(e))
    }
  }

  const createDashboard = async (input: IDashboardCreateInput) => {
    try {
      const response = await client.mutate<Record<'dashboardCreate', IDashboard>, IDashboardCreateMutationArgs>({
        mutation: DASHBOARD_CREATE_MUTATION,
        variables: {
          input
        }
      })

      return response.data.dashboardCreate
    } catch (e) {
      console.log('[useDashboardsAPI.createDashboard]', parseGraphQLErrorMessages(e))
    }
  }

  const updateDashboard = async (input: IDashboardUpdateInput) => {
    try {
      return await client.mutate<IDashboard, IDashboardUpdateMutationArgs>({
        mutation: DASHBOARD_UPDATE_MUTATION,
        variables: {
          input
        }
      })
    } catch (e) {
      console.log('[useDashboardsAPI.updateDashboard]', parseGraphQLErrorMessages(e))
    }
  }

  const deleteDashboard = async (input: IDashboardDeleteInput) => {
    try {
      await client.mutate<IDashboard, IDashboardDeleteMutationArgs>({
        mutation: DASHBOARD_DELETE_MUTATION,
        variables: {
          input
        }
      })
    } catch (e) {
      console.log('[useDashboardsAPI.updateDashboard]', parseGraphQLErrorMessages(e))
    }
  }

  const createDashboardItem = async (input: IDashboardItemCreateInput) => {
    try {
      const response = await client.mutate<Record<'dashboardItemCreate', IDashboardItem>, IDashboardItemCreateMutationArgs>({
        mutation: DASHBOARD_ITEM_CREATE_MUTATION,
        variables: {
          input
        }
      })

      return response.data.dashboardItemCreate
    } catch (e) {
      console.log('[useDashboardsAPI.createDashboard]', parseGraphQLErrorMessages(e))
    }
  }

  const updateDashboardItem = async (input: IDashboardItemUpdateInput) => {
    try {
      await client.mutate<IDashboardItem, IDashboardItemUpdateMutationArgs>({
        mutation: DASHBOARD_ITEM_UPDATE_MUTATION,
        variables: {
          input
        }
      })
    } catch (e) {
      console.log('[useDashboardsAPI.createDashboard]', parseGraphQLErrorMessages(e))
    }
  }

  const deleteDashboardItem = async (id: string) => {
    try {
      await client.mutate<IMutationResponse, IDashboardItemDeleteMutationArgs>({
        mutation: DASHBOARD_ITEM_DELETE_MUTATION,
        variables: {
          input: {
            id
          }
        }
      })
    } catch (e) {
      console.log('[useDashboardsAPI.createDashboard]', parseGraphQLErrorMessages(e))
    }
  }

  return {
    getDashboards,
    createDashboard,
    updateDashboard,
    deleteDashboard,
    getDashboardItems,
    createDashboardItem,
    updateDashboardItem,
    deleteDashboardItem
  }
}
